import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/Combobox";
import { ConflictPanel } from "@/components/ConflictPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { combine, timeOf, snapTo15, SectionLabel, Field } from "@/components/shift/shiftUi";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Linkify } from "@/components/Linkify";
import { ParticipantsBilling } from "@/components/shift/ParticipantsBilling";
import { CostBreakdown } from "@/components/shift/CostBreakdown";
import { LifecycleActions } from "@/components/shift/LifecycleActions";
import { NotifyCheckboxes, toastNotifications } from "@/components/shift/NotifyCheckboxes";
import { ShiftApplications, ShiftAudit } from "@/components/shift/ShiftActivity";
import { ShiftDialogs } from "@/components/shift/ShiftDialogs";
import { api } from "@/lib/api";
import { fmtLogFull, orgToday } from "@/lib/tz";
import { ShiftNoteAttachments } from "@/components/ShiftNoteAttachments";
import { useAuth } from "@/context/AuthContext";
import { BudgetCheckDialog } from "@/components/BudgetCheckDialog";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Save, X, Plus, Lock, StickyNote, Link2, ExternalLink, Car } from "lucide-react";

export function ShiftDrawer({ open, onOpenChange, shiftName, defaults, participants, staff, services, itemGroups = [], onSaved, onOpenShift }) {
  const { can: canAuth } = useAuth();
  const isCreate = !shiftName;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shift, setShift] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [linkedLegs, setLinkedLegs] = useState([]);
  const [shiftNotes, setShiftNotes] = useState([]);
  const [reasonDlg, setReasonDlg] = useState(null); // {action,title,onConfirm}
  const [reason, setReason] = useState("");
  const [cancelCharge, setCancelCharge] = useState(false);
  const [cancelInitiator, setCancelInitiator] = useState("");
  const [cancelNotify, setCancelNotify] = useState({ staff: false, participant: false });
  const [assignNotify, setAssignNotify] = useState({ staff: false, participant: false });
  const [savedWorkers, setSavedWorkers] = useState([]); // workers already on the saved shift
  const [addresses, setAddresses] = useState([]);
  const [locOther, setLocOther] = useState(false);
  const [endOther, setEndOther] = useState(false);
  const [quote, setQuote] = useState(null);
  const [repeat, setRepeat] = useState({ on: false, frequency: "weekly", interval: 1, until: "", monthly_mode: "day_of_month", weekdays: [] });
  const [dupDlg, setDupDlg] = useState(null);
  const [scopeDlg, setScopeDlg] = useState(false);
  const [budgetDlg, setBudgetDlg] = useState(null);     // funding decision needed (service agreement budget)
  const budgetRes = useRef([]);
  const [includeWorker, setIncludeWorker] = useState(false);
  const [delSeriesDlg, setDelSeriesDlg] = useState(null);
  const [cloneDlg, setCloneDlg] = useState(null); // {date}
  const [cloning, setCloning] = useState(false);
  const [officeNote, setOfficeNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [maybeAck, setMaybeAck] = useState(false);
  const [properties, setProperties] = useState([]);
  const [kmWarn, setKmWarn] = useState(false);
  const partsSnapshotRef = useRef(null);

  // Lock: invoiced shifts unlock only by voiding the invoice; approved shifts unlock
  // only by unapproving (managers). While locked, the whole drawer is read-only.
  const shiftInvoiced = ["Invoiced", "Submitted"].includes(shift?.custom_invoice_status);
  const shiftApproved = shift?.timesheet_status === "Approved";
  const shiftLocked = !isCreate && (shiftInvoiced || shiftApproved);
  // A locked (approved/invoiced) shift can't be changed here, whatever the role allows.
  const can = shiftLocked ? { ...canAuth, has: () => false } : canAuth;
  const canEdit = can.has("roster.edit");

  const [form, setForm] = useState({
    workers: [], participants: [], service_item: "", location: "",
    end_address_enabled: false, end_address: "",
    shift_date: "", start: "", end: "", km_authorised: 0, job_board_open: false, job_board_reason: "",
    jb_target_genders: [], jb_preferred_only: false,
    is_sleepover: false, sleep_start: "22:00", sleep_end: "06:00", billing_workers_mode: "", sleepover_each_worker: false,
    sleep_override_reason: "",
    shift_instructions: "", tasks: [], sil_property: "",
  });

  useEffect(() => {
    if (open) api.properties({ status: "Active" }).then(setProperties).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSavedWorkers([]);
    setAssignNotify({ staff: false, participant: false });
    setShift(null);
    setConflicts(null);
    setLinkedLegs([]);
    setQuote(null);
    kmSigRef.current = null;
    kmAllowedRef.current = null;
    setKmWarn(false);
    setRepeat({ on: false, frequency: "weekly", interval: 1, until: "", monthly_mode: "day_of_month", weekdays: [] });
    setDupDlg(null); setScopeDlg(false); setDelSeriesDlg(null); setCloneDlg(null);
    setBudgetDlg(null); budgetRes.current = [];
    // Fix: always reset address display-mode flags so stale state from a previously
    // viewed shift never bleeds into the next one (Bug 2 — state not reset on re-open).
    setLocOther(false);
    setEndOther(false);
    if (isCreate) {
      setShift(null);
      setForm({
        workers: defaults?.worker ? [defaults.worker] : [],
        participants: defaults?.participant ? [{ participant: defaults.participant, participant_name: "", billing_type: "", billing_ratio: 1, from: "", to: "", ratioLocked: false }] : [],
        no_participant: !!defaults?.no_participant, internal_billing: "",
        service_item: "", location: "",
        end_address_enabled: false, end_address: "",
        shift_date: defaults?.date || orgToday(),
        start: "09:00", end: "12:00", km_authorised: 0, job_board_open: false, job_board_reason: "",
        jb_target_genders: [], jb_preferred_only: false,
        is_sleepover: false, sleep_start: "22:00", sleep_end: "06:00", billing_workers_mode: "", sleepover_each_worker: false,
        sleep_override_reason: "",
        shift_instructions: "", tasks: [], sil_property: defaults?.sil_property || "",
      });
    } else {
      loadShift();
    }
    // eslint-disable-next-line
  }, [open, shiftName]);

  const loadShift = async () => {
    setLoading(true);
    try {
      const { shift, conflicts, linked_legs } = await api.shift(shiftName);
      setShift(shift);
      setSavedWorkers((shift.shift_workers || []).map((w) => w.support_worker).filter(Boolean).concat(shift.support_worker ? [shift.support_worker] : []));
      setConflicts(conflicts);
      setLinkedLegs(linked_legs || []);
      api.shiftNotes(shiftName).then(setShiftNotes).catch(() => setShiftNotes([]));
      setForm({
        workers: (shift.shift_workers || []).map((w) => w.support_worker).filter(Boolean).length
          ? (shift.shift_workers || []).map((w) => w.support_worker).filter(Boolean)
          : (shift.support_worker ? [shift.support_worker] : []),
        participants: (shift.shift_participants && shift.shift_participants.length)
          ? shift.shift_participants.map((p) => ({ participant: p.participant, participant_name: p.participant_name, billing_type: p.billing_type || "", billing_ratio: p.billing_ratio || 1, from: timeOf(p.start_time), to: timeOf(p.end_time), ratioLocked: true }))
          : (shift.participant ? [{ participant: shift.participant, participant_name: shift.participant_name, billing_type: shift.primary_billing_type || "", billing_ratio: shift.billing_ratio || 1, from: "", to: "", ratioLocked: true }] : []),
        no_participant: !!shift.no_participant,
        internal_billing: shift.no_participant ? (shift.primary_billing_type || "") : "",
        service_item: shift.service_item || "", location: shift.location || "",
        end_address_enabled: !!shift.end_address_enabled, end_address: shift.end_address || "",
        shift_date: shift.shift_date, start: timeOf(shift.start_time), end: timeOf(shift.end_time),
        km_authorised: shift.km_authorised || 0, job_board_open: !!shift.job_board_open, job_board_reason: shift.job_board_reason || "",
        jb_target_genders: (shift.jb_target_gender || "").split(",").map((g) => g.trim()).filter(Boolean), jb_preferred_only: !!shift.jb_preferred_only,
        is_sleepover: !!shift.is_sleepover, sleep_start: timeOf(shift.sleep_start_time) || "22:00", sleep_end: timeOf(shift.sleep_end_time) || "06:00",
        billing_workers_mode: shift.billing_workers_mode || "", sleepover_each_worker: !!shift.sleepover_each_worker,
        sleep_override_reason: "",
        shift_instructions: shift.shift_instructions || "",
        tasks: (shift.tasks || []).map((t) => ({ title: t.title || "", completed: !!t.completed, notes: t.notes || "" })),
        sil_property: shift.sil_property || "",
      });
    } catch {
      toast.error("Failed to load shift");
    } finally {
      setLoading(false);
    }
  };

  const primaryParticipant = form.participants[0]?.participant || "";

  useEffect(() => {
    if (!open || !primaryParticipant) { setAddresses([]); return; }
    api.participantAddresses(primaryParticipant).then((addrs) => {
      setAddresses(addrs);
      // Fix (Bug 3): read end_address from inside the functional-updater `f` argument so we
      // always have the current state value, not a stale closure capture from render time.
      setForm((f) => {
        // Resolve endOther: free-text mode when end_address is set but not in the saved list.
        const ea = (f.end_address || "").trim();
        setEndOther(!!(ea && !addrs.some((a) => a.address === ea)));

        if (!addrs.length) return f;

        // Fix (Bug 1): For existing shifts the saved location (f.location) is authoritative —
        // check whether it appears in the participant's address list to determine display mode,
        // but NEVER overwrite a non-empty location loaded from the DB with the participant's
        // primary address.  Only auto-fill when creating a new shift or the location is blank.
        if (f.location) {
          const inList = addrs.some((a) => a.address === f.location);
          if (inList) {
            console.log(`[ShiftDrawer] location display: list-select — "${f.location}" matched saved address for ${primaryParticipant}`);
            setLocOther(false);
          } else {
            console.log(`[ShiftDrawer] location display: free-text — "${f.location}" not in saved addresses for ${primaryParticipant} (address may have been manually entered or removed from participant profile)`);
            setLocOther(true);
          }
          return f; // Never overwrite an existing location value
        }

        // Location is blank — auto-fill with the participant's primary address (new shifts / empty state).
        const prim = addrs.find((a) => a.is_primary) || addrs[0];
        console.log(`[ShiftDrawer] location display: auto-filled primary — "${prim.address}" for ${primaryParticipant} (location was empty)`);
        setLocOther(false);
        return { ...f, location: prim.address };
      });
    }).catch(() => setAddresses([]));
    // eslint-disable-next-line
  }, [primaryParticipant, open]);

  const pName = (id) => participants.find((p) => p.name === id)?.display_name || id || "";
  const wName = (id) => staff.find((s) => s.name === id)?.display_name || id || "";

  const participantsPayload = () => form.participants
    .filter((p) => p.participant)
    .map((p) => ({ participant: p.participant, participant_name: p.participant_name || pName(p.participant),
                   billing_type: p.billing_type || null, billing_ratio: Number(p.billing_ratio) || 1,
                   start_time: p.from ? combine(form.shift_date, p.from) : null,
                   end_time: p.to ? combine(form.shift_date, p.to) : null }));

  const body = () => ({
    name: shiftName || undefined,
    participant: form.no_participant ? null : primaryParticipant, support_worker: form.workers[0] || null,
    no_participant: !!form.no_participant,
    primary_billing_type: form.no_participant ? (form.internal_billing || null) : undefined,
    workers: form.workers, participants: form.no_participant ? [] : participantsPayload(),
    sil_property: form.no_participant ? null : (form.sil_property || null),
    service_item: form.service_item, location: form.location,
    shift_instructions: (form.shift_instructions || "").trim(),
    tasks: (form.tasks || []).filter((t) => (t.title || "").trim()).map((t) => ({ title: t.title.trim(), completed: !!t.completed, notes: t.notes || "" })),
    end_address_enabled: form.end_address_enabled,
    end_address: form.end_address_enabled ? (form.end_address || "").trim() : null,
    shift_date: form.shift_date, start_time: combine(form.shift_date, form.start),
    end_time: combine(form.shift_date, form.end), km_authorised: Math.max(0, Number(form.km_authorised) || 0),
    job_board_open: form.job_board_open,
    job_board_reason: (form.job_board_reason || "").trim() || null,
    jb_target_gender: form.job_board_open ? (form.jb_target_genders || []) : [],
    jb_preferred_only: form.job_board_open ? !!form.jb_preferred_only : false,
    is_sleepover: form.is_sleepover,
    sleep_start_time: form.is_sleepover ? combine(form.shift_date, form.sleep_start) : null,
    sleep_end_time: form.is_sleepover ? combine(form.shift_date, form.sleep_end) : null,
    sleep_override_reason: form.is_sleepover && sleepReasonRequired ? (form.sleep_override_reason || "").trim() : null,
    billing_workers_mode: form.billing_workers_mode || "",
    sleepover_each_worker: !!form.sleepover_each_worker,
    maybe_available_ack: maybeAck,
    budget_resolutions: budgetRes.current.length ? budgetRes.current : undefined,
  });

  const partKey = JSON.stringify(form.participants);
  const workersKey = JSON.stringify(form.workers);

  // Drive time from/to the worker's adjacent shifts — shown before saving so the
  // planner sees the travel implication as soon as a worker is picked.
  const [travel, setTravel] = useState(null);
  useEffect(() => {
    const worker = form.workers[0];
    if (!open || !worker || !form.shift_date || !form.start || !form.end) { setTravel(null); return; }
    const t = setTimeout(() => {
      api.travelCheck({
        support_worker: worker, shift_date: form.shift_date,
        start_time: combine(form.shift_date, form.start),
        end_time: combine(form.shift_date, form.end),
        location: form.location || "",
        exclude: shiftName || undefined,
      }).then(setTravel).catch(() => setTravel(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, shiftName, form.shift_date, form.start, form.end, form.location, workersKey]);

  // Live billing quote
  useEffect(() => {
    const hasBilling = form.no_participant ? !!form.internal_billing : form.participants.length > 0;
    if (!open || !hasBilling || !form.shift_date || !form.start || !form.end) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.billingQuote({
        name: shiftName || null,
        participants: form.no_participant ? [] : participantsPayload(),
        primary_billing_type: form.no_participant ? form.internal_billing : undefined,
        is_sleepover: form.is_sleepover,
        sleep_start_time: form.is_sleepover ? combine(form.shift_date, form.sleep_start) : null,
        sleep_end_time: form.is_sleepover ? combine(form.shift_date, form.sleep_end) : null,
        shift_date: form.shift_date, start_time: combine(form.shift_date, form.start),
        end_time: combine(form.shift_date, form.end), support_worker: form.workers[0] || null,
        workers: form.workers, billing_workers_mode: form.billing_workers_mode || "",
        sleepover_each_worker: !!form.sleepover_each_worker,
      }).then(setQuote).catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, form.is_sleepover, form.sleep_start, form.sleep_end, form.shift_date, form.start, form.end,
      workersKey, partKey, form.no_participant, form.internal_billing, form.billing_workers_mode,
      form.sleepover_each_worker]);

  // Auto conflict check — runs live as the shift is built (no manual button).
  useEffect(() => { setMaybeAck(false); }, [workersKey, shiftName, open]);
  useEffect(() => {
    if (!open || !(primaryParticipant || (form.no_participant && form.internal_billing)) || !form.shift_date || !form.start || !form.end) { setConflicts(null); return; }
    const t = setTimeout(() => {
      api.previewConflicts(body()).then(setConflicts).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, primaryParticipant, form.shift_date, form.start, form.end, form.location,
      form.km_authorised, form.is_sleepover, form.sleep_start, form.sleep_end, workersKey, partKey,
      form.no_participant, form.internal_billing]);

  const kmAllowed = quote ? quote.km_allowed : false;

  // How many transport km the participant's service agreement has left (office only; just a heads-up).
  const [kmBudget, setKmBudget] = useState(null);
  useEffect(() => {
    if (!open || !canEdit || !primaryParticipant || form.no_participant || !kmAllowed) { setKmBudget(null); return; }
    let live = true;
    api.participantBudget(primaryParticipant).then((b) => live && setKmBudget(b?.tracked ? b.transport : null)).catch(() => live && setKmBudget(null));
    return () => { live = false; };
  }, [open, canEdit, primaryParticipant, form.no_participant, kmAllowed]);
  // This shift's own saved allowance is already counted in "remaining" until the shift has been worked.
  const kmSaved = shift && shift.status !== "Completed" ? Number(shift.km_authorised) || 0 : 0;
  const kmOver = kmBudget ? (Number(form.km_authorised) || 0) - kmSaved - kmBudget.remaining : 0;

  // R4: warn (non-blocking) only when the user CHANGES the billing selection from
  // one that allows KM to one that doesn't, while KM are entered. Keyed on the
  // billing-type signature so ordinary quote recomputes (time/worker/date edits,
  // loading states, cross-shift ref leakage) never trip it — that was the bug
  // where Community Access shifts wrongly showed "ABT not available".
  const billingSig = form.participants.map((p) => p.billing_type || "").join("|");
  const kmSigRef = useRef(null);
  const kmAllowedRef = useRef(null);
  useEffect(() => {
    if (!quote) return;
    const prevSig = kmSigRef.current, prevAllowed = kmAllowedRef.current;
    kmSigRef.current = billingSig;
    kmAllowedRef.current = quote.km_allowed;
    if (prevSig !== null && prevSig !== billingSig && prevAllowed === true &&
        quote.km_allowed === false && Number(form.km_authorised) > 0 && !kmWarn) {
      setKmWarn(true);
    }
    // eslint-disable-next-line
  }, [quote]);

  // ---- participant/worker helpers ----
  const addParticipant = (pid) => {
    if (!pid) return;
    setForm((f) => {
      if (f.participants.some((p) => p.participant === pid)) return f;
      const rows = [...f.participants, { participant: pid, participant_name: pName(pid), billing_type: "", billing_ratio: 1, from: "", to: "", ratioLocked: false }];
      const n = new Set(rows.map((r) => r.participant)).size;
      return { ...f, participants: rows.map((r) => r.ratioLocked ? r : { ...r, billing_ratio: n }) };
    });
  };
  const removeParticipant = (i) => setForm((f) => {
    const rows = f.participants.filter((_, j) => j !== i);
    const n = new Set(rows.map((r) => r.participant)).size || 1;
    return { ...f, participants: rows.map((r) => r.ratioLocked ? r : { ...r, billing_ratio: n }) };
  });
  const splitParticipant = (i) => setForm((f) => {
    const src = f.participants[i];
    const seg = { participant: src.participant, participant_name: src.participant_name,
                  billing_type: src.billing_type, billing_ratio: src.billing_ratio,
                  from: src.to || "", to: "", ratioLocked: true };
    const rows = [...f.participants.slice(0, i + 1), seg, ...f.participants.slice(i + 1)];
    return { ...f, participants: rows };
  });
  const setPart = (i, key, val) => setForm((f) => {
    if (key === "billing_type") partsSnapshotRef.current = f.participants;
    const rows = [...f.participants];
    rows[i] = { ...rows[i], [key]: val, ...(key === "billing_ratio" ? { ratioLocked: true } : {}) };
    return { ...f, participants: rows };
  });
  // Keep a snapshot of participants for potential rollback when KM warning cancels
  useEffect(() => {
    partsSnapshotRef.current = form.participants;
  }, [form.participants]);
  const addWorker = (wid) => { if (wid) setForm((f) => f.workers.includes(wid) ? f : { ...f, workers: [...f.workers, wid] }); };
  const removeWorker = (wid) => setForm((f) => ({ ...f, workers: f.workers.filter((w) => w !== wid) }));

  // Selecting a SIL house auto-links all its active tenants as participant rows
  // (billing type left blank for the coordinator to set per participant).
  const pickSil = async (pid) => {
    setForm((f) => ({ ...f, sil_property: pid || "" }));
    if (!pid) return;
    try {
      const doc = await api.property(pid);
      const tenants = (doc.tenancies || []).filter((t) => t.status !== "Ended" && t.participant);
      setForm((f) => {
        const existing = new Set(f.participants.map((p) => p.participant));
        const added = tenants.filter((t) => !existing.has(t.participant)).map((t) => ({
          participant: t.participant, participant_name: t.participant_name || pName(t.participant),
          billing_type: "", billing_ratio: 1, from: "", to: "", ratioLocked: false,
        }));
        const rows = [...f.participants, ...added];
        const n = new Set(rows.map((r) => r.participant)).size || 1;
        const loc = (!f.location && doc.address_line1) ? [doc.address_line1, doc.suburb, doc.state, doc.postcode].filter(Boolean).join(", ") : f.location;
        return { ...f, participants: rows.map((r) => r.ratioLocked ? r : { ...r, billing_ratio: n }), location: loc };
      });
      if (tenants.length) toast.success(`Linked ${tenants.length} participant${tenants.length > 1 ? "s" : ""} from ${doc.property_name}`);
    } catch { toast.error("Could not load SIL house"); }
  };

  // Auto-populate participants when the drawer is opened from a SIL roster row.
  useEffect(() => {
    if (open && isCreate && defaults?.sil_property) pickSil(defaults.sil_property);
    // eslint-disable-next-line
  }, [open]);

  const toggleSleepover = (v) => setForm((f) => ({ ...f, is_sleepover: v,
    sleep_start: f.sleep_start || "22:00", sleep_end: f.sleep_end || "06:00" }));
  // NDIS sleepover must be EXACTLY 8h. Non-8h is a hard block unless a manager/admin
  // overrides with a written justification.
  const sleepMins = (() => {
    if (!form.is_sleepover || !form.sleep_start || !form.sleep_end) return 480;
    const [sh, sm] = form.sleep_start.split(":").map(Number);
    const [eh, em] = form.sleep_end.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60;
    return mins;
  })();
  const sleepGapExact = !form.is_sleepover || sleepMins === 480;
  const sleepReasonRequired = !sleepGapExact;
  const sleepBlocked = sleepReasonRequired && (!can.has("roster.override") || !(form.sleep_override_reason || "").trim());

  const seriesId = shift?.repeat_series_id || "";

  // A 409 "budget_check" means the save needs a funding decision: ask, then retry with the answer.
  const askBudget = (e, retry) => {
    const d = e.response?.data;
    if (e.response?.status !== 409 || d?.code !== "budget_check") return false;
    setScopeDlg(false); setDupDlg(null);
    setBudgetDlg({ issues: d.issues, retry });
    return true;
  };

  const doCreate = async () => {
    setSaving(true);
    try {
      const created = await api.createShift(body(), assignNotify);
      toast.success("Shift created");
      toastNotifications(created?.notifications);
      onSaved?.(); onOpenChange(false);
    } catch (e) { if (!askBudget(e, () => doCreate())) toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const doRepeatCreate = async (action) => {
    setSaving(true);
    try {
      const res = await api.createRepeating({
        ...body(),
        repeat: { frequency: repeat.frequency, interval: Number(repeat.interval) || 1,
                  until: repeat.until, monthly_mode: repeat.monthly_mode,
                  weekdays: repeat.frequency === "weekly" ? repeat.weekdays : null,
                  duplicate_action: action },
      });
      if (res.status === "duplicates") { setSaving(false); setDupDlg(res); return; }
      toast.success(`Created ${res.created} repeating shift${res.created === 1 ? "" : "s"}${res.skipped?.length ? ` · skipped ${res.skipped.length}` : ""}`);
      setDupDlg(null); onSaved?.(); onOpenChange(false);
    } catch (e) { if (!askBudget(e, () => doRepeatCreate(action))) toast.error(e.response?.data?.detail || "Failed to create repeating shifts"); }
    finally { setSaving(false); }
  };

  const addOfficeNote = async () => {
    const note = officeNote.trim();
    if (!note) return toast.error("Enter a note first");
    setAddingNote(true);
    try {
      const { office_notes } = await api.addOfficeNote(shiftName, note);
      setShift((s) => ({ ...s, office_notes }));
      setOfficeNote("");

      toast.success("Office note added to the audit log");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
    finally { setAddingNote(false); }
  };

  const doClone = async () => {
    if (!cloneDlg?.date) return toast.error("Pick a target date");
    setCloning(true);
    try {
      const doc = await api.cloneShift(shiftName, cloneDlg.date, budgetRes.current);
      toast.success(`Shift cloned to ${cloneDlg.date}${doc?.name ? ` (${doc.name})` : ""}`);
      setCloneDlg(null); onSaved?.(); onOpenChange(false);
    } catch (e) { if (!askBudget(e, () => doClone())) toast.error(e.response?.data?.detail || "Clone failed"); }
    finally { setCloning(false); }
  };

  // Regex for detecting URLs in shift instructions
  const urlRegex = /(https?:\/\/|www\.)/i;
  
  const doUpdate = async (scope, incWorker = false) => {
    setSaving(true);
    try {
      const res = await api.updateShift(shiftName, body(), scope, incWorker, assignNotify);
      toast.success(scope === "future"
        ? `Updated this shift and ${res.series_propagated || 0} future repeat${res.series_propagated === 1 ? "" : "s"}${incWorker ? " (worker applied)" : ""}`
        : "Shift updated");
      toastNotifications(res?.notifications);
      if (res.series_held?.length) toast.warning(`${res.series_held.length} published repeat${res.series_held.length === 1 ? " was" : "s were"} not changed: the change would bring a hard block onto ${res.series_held.length === 1 ? "it" : "them"}. Open ${res.series_held.length === 1 ? "it" : "each one"} to review.`);
      setScopeDlg(false); onSaved?.(); onOpenChange(false);
    } catch (e) { if (!askBudget(e, () => doUpdate(scope, incWorker))) toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const doDeleteSeries = async (fromDate) => {
    try {
      const r = await api.deleteSeries(seriesId, fromDate);
      toast.success(`Deleted ${r.deleted} upcoming repeating shift${r.deleted === 1 ? "" : "s"} (from ${r.cutoff})`);
      if (r.skipped?.length) toast.warning(`${r.skipped.length} kept: published, approved, invoiced or linked shifts aren't deleted. Unpublish them first, or delete them one by one.`);
      setDelSeriesDlg(null); onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const save = async () => {
    if (form.no_participant) {
      if (!form.internal_billing) return toast.error("Choose Staff Training or Office Shift");
    } else if (!primaryParticipant) return toast.error("Add at least one participant");
    if (!(form.location || "").trim()) return toast.error("Location is required");
    if (maybeBlocked) return toast.error("Confirm you've spoken with the 'maybe available' worker before saving");
    if (sleepReasonRequired && !can.has("roster.override"))
      return toast.error("Sleepover must span exactly 8 hours. A Rostering Manager can override.");
    if (sleepReasonRequired && !(form.sleep_override_reason || "").trim())
      return toast.error("A written justification is required to override the 8-hour sleepover rule.");
    if (isCreate && repeat.on) {
      if (!repeat.until) return toast.error("Choose a 'repeat until' date");
      if (dayjs(repeat.until).isBefore(dayjs(form.shift_date), "day"))
        return toast.error("'Repeat until' must be on or after the shift date");
      return doRepeatCreate("warn");
    }
    if (isCreate) return doCreate();
    // Editing: prompt scope for any series shift so editors can apply the change
    // (instructions, tasks, times, billing…) to this shift or all future repeats.
    if (seriesId && canEdit) { setIncludeWorker(false); return setScopeDlg(true); }
    return doUpdate("this");
  };

  const doPublish = async () => {
    if (maybeBlocked) return toast.error("Confirm you've spoken with the 'maybe available' worker before publishing");
    const noWorker = !(form.workers && form.workers.length) && !shift?.support_worker;
    if (noWorker && !window.confirm("This shift has no assigned support worker. Publish it to the unassigned queue anyway?"))
      return;
    try {
      await api.publish(shiftName);
      toast.success(noWorker ? "Shift published (unassigned)" : "Shift published");
      onSaved?.(); loadShift();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "object" && d?.conflicts) {
        setConflicts(d.conflicts);
        toast.error("Hard blocks prevent publishing — override required");
      } else toast.error(d || "Publish failed");
    }
  };

  const doUnpublish = async () => {
    if (!window.confirm("Unpublish this shift? It will be pulled back from workers and returned to an editable (unpublished) state. The assigned worker is kept."))
      return;
    try {
      await api.unpublish(shiftName);
      toast.success("Shift unpublished — pulled back for editing");
      onSaved?.(); loadShift();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Unpublish failed");
    }
  };

  const runAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); onSaved?.(); loadShift(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const [linkCands, setLinkCands] = useState(null); // null = not loaded, [] = none, [...] = options
  const [linking, setLinking] = useState(false);
  const loadLinkCands = async () => {
    try { setLinkCands(await api.linkCandidates(shiftName)); }
    catch { setLinkCands([]); }
  };
  const doLink = async (other) => {
    setLinking(true);
    try {
      await api.linkShift(shiftName, other);
      toast.success("Shifts linked into one package (shared worker, combined travel rule)");
      setLinkCands(null); onSaved?.(); loadShift();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link shifts"); }
    finally { setLinking(false); }
  };
  const doUnlink = async () => {
    if (!window.confirm("Unlink this shift from its package? It becomes a standalone shift again and the combined provider-travel rule no longer applies.")) return;
    setLinking(true);
    try {
      await api.unlinkShift(shiftName);
      toast.success("Shift unlinked");
      setLinkCands(null); onSaved?.(); loadShift();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not unlink"); }
    finally { setLinking(false); }
  };
  const [lrepeat, setLrepeat] = useState(null); // null=closed, else config object
  const [lrepeatDup, setLrepeatDup] = useState(null); // duplicate-resolution prompt
  // Format the lrepeatDup dates for display
  const lrepeatDupDates = lrepeatDup ? lrepeatDup.dates.join(", ") : "";
  const openLinkRepeat = () => setLrepeat({
    frequency: "weekly", interval: 2, until: "", monthly_mode: "day_of_month",
    weekdays: shift?.shift_date ? [dayjs(shift.shift_date).day() === 0 ? 6 : dayjs(shift.shift_date).day() - 1] : [],
  });
  const doLinkRepeat = async (action) => {
    setLinking(true);
    setLrepeatDup(null);
    try {
      const res = await api.linkRepeat(shiftName, {
        frequency: lrepeat.frequency, interval: Number(lrepeat.interval) || 1,
        until: lrepeat.until, monthly_mode: lrepeat.monthly_mode,
        weekdays: lrepeat.frequency === "weekly" ? lrepeat.weekdays : null,
        duplicate_action: action,
      });
      if (res.status === "duplicates") {
        setLinking(false);
        setLrepeatDup({ dates: res.duplicates, total: res.total });
        return;
      }
      const bits = [];
      if (res.created) bits.push(`${res.created} created`);
      if (res.adopted) bits.push(`${res.adopted} linked from existing`);
      if (res.skipped?.length) bits.push(`${res.skipped.length} skipped`);
      toast.success(`${res.groups} recurring package${res.groups === 1 ? "" : "s"}${bits.length ? ` · ${bits.join(", ")}` : ""}`);
      setLrepeat(null); onSaved?.(); loadShift();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the recurring package"); }
    finally { setLinking(false); }
  };

  const openReason = (action, title, onConfirm) => { setReason(""); setCancelCharge(false); setCancelInitiator(""); setCancelNotify({ staff: false, participant: false }); setReasonDlg({ action, title, onConfirm }); };
  const confirmReason = async () => {
    if (!reason.trim()) return toast.error("A reason is mandatory");
    if (reasonDlg?.action === "cancel" && !cancelInitiator) return toast.error("Please select who initiated this cancellation");
    try {
      const res = await reasonDlg.onConfirm(reason.trim(), cancelCharge, cancelInitiator, cancelNotify);
      toast.success(`${reasonDlg.title} recorded`);
      toastNotifications(res?.notifications);
      setReasonDlg(null); onSaved?.();
      if (!res?.closed) loadShift();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pOpts = participants.map((p) => ({ value: p.name, label: p.display_name || p.name, hint: p.status }));
  const wOpts = staff.filter((s) => s.status === "Active").map((s) => ({ value: s.name, label: s.display_name || s.name, hint: s.primary_role }));
  // Job Board gender chips: the genders active staff actually have (with counts), so a
  // posting can't target a spelling nobody matches. Matching ignores case/hyphens, like
  // the backend; a gender saved on the shift that no active worker has still shows.
  const gKey = (g) => (g || "").replace(/-/g, " ").split(/\s+/).filter(Boolean).join(" ").toLowerCase();
  const activeWorkers = staff.filter((s) => s.status === "Active" && /support worker/i.test(s.primary_role || ""));
  const genderOpts = [];
  [...activeWorkers.map((s) => s.gender), ...(form.jb_target_genders || [])].forEach((g) => {
    if (!gKey(g)) return;
    const o = genderOpts.find((x) => x.key === gKey(g));
    if (!o) genderOpts.push({ key: gKey(g), label: g.trim(), count: activeWorkers.filter((s) => gKey(s.gender) === gKey(g)).length });
  });
  genderOpts.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const noGenderCount = activeWorkers.filter((s) => !gKey(s.gender)).length;
  const status = shift?.status;
  const hasBlocks = conflicts?.has_blocks;
  const maybeAvailable = !!(conflicts?.warnings || []).some((w) => w.code === "maybe_available");
  const maybeBlocked = maybeAvailable && !maybeAck;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="shift-drawer">
        <SheetHeader className="px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base font-bold text-slate-900">
              {isCreate ? "New Shift" : shift?.title || shiftName}
            </SheetTitle>
            {status && <StatusBadge status={status} worker={shift?.support_worker} />}
          </div>
          {!isCreate && <div className="text-[11px] text-slate-400 font-mono">{shiftName}</div>}
        </SheetHeader>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="p-5 space-y-5">
            {shiftLocked && (
              <div className={`rounded-md border px-3 py-2.5 flex items-start gap-2.5 ${shiftInvoiced ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`} data-testid="shift-lock-banner">
                <Lock className={`h-4 w-4 mt-0.5 shrink-0 ${shiftInvoiced ? "text-rose-600" : "text-amber-600"}`} />
                <div className="min-w-0">
                  <div className={`text-xs font-semibold ${shiftInvoiced ? "text-rose-800" : "text-amber-800"}`}>
                    {shiftInvoiced ? "Invoiced — locked for editing" : "Approved — locked for editing"}
                  </div>
                  <div className={`text-[11px] leading-snug ${shiftInvoiced ? "text-rose-700" : "text-amber-700"}`}>
                    {shiftInvoiced
                      ? "This shift is on an invoice. Void the invoice (Invoicing → billing runs) before it can be changed."
                      : "This timesheet is approved. Unapprove it to make changes — this returns it to the Approve queue."}
                  </div>
                  {shiftApproved && !shiftInvoiced && canAuth.has("timesheets.unapprove") && (
                    <Button variant="outline" size="sm" className="mt-2 h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
                      onClick={() => openReason("unapprove", "Unapproved", (r) => api.unapproveTimesheet(shiftName, r))}
                      data-testid="unapprove-shift">
                      Unapprove timesheet
                    </Button>
                  )}
                </div>
              </div>
            )}
            {/* Conflicts */}
            {conflicts && (
              <div>
                <SectionLabel>Conflict Check</SectionLabel>
                <ConflictPanel conflicts={conflicts} />
                {maybeAvailable && (
                  <label className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 cursor-pointer" data-testid="maybe-available-ack">
                    <input type="checkbox" checked={maybeAck} onChange={(e) => setMaybeAck(e.target.checked)}
                      className="mt-0.5 accent-amber-600" data-testid="maybe-available-ack-check" />
                    <span className="text-[11px] text-amber-800 leading-snug">
                      I've spoken with this worker and they've agreed to work during their <b>“maybe available”</b> period.
                      <span className="block text-amber-600 mt-0.5">Required before saving or publishing. This is recorded on the shift audit trail.</span>
                    </span>
                  </label>
                )}
                {hasBlocks && !shift?.override_active && can.has("roster.override") && !isCreate && (
                  <Button variant="outline" size="sm" data-testid="override-open"
                    className="mt-2 h-8 text-xs border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => openReason("override", "Override", (r) => api.override(shiftName, r))}>
                    <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Override hard blocks (Manager)
                  </Button>
                )}
                {!!shift?.override_pending && !shift?.override_active && (
                  <div className="mt-2 text-[11px] rounded bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5" data-testid="shift-held">
                    <b>Held for a Rostering Manager.</b> {shift.override_pending_reason ? `${shift.override_pending_reason}. ` : ""}
                    The worker isn't told and can't see or clock this shift until a manager overrides{can.has("roster.override") ? " (use Override hard blocks above)" : ""}.
                  </div>
                )}
                {!!shift?.override_active && (
                  <div className="mt-2 text-[11px] rounded bg-red-50 border border-red-100 text-red-700 px-2 py-1.5">
                    Override active by {shift.overridden_by}: “{shift.override_reason}”
                  </div>
                )}
              </div>
            )}

            {/* Form */}
            <div className="space-y-3">
              <SectionLabel>Support Workers</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {form.workers.map((w) => (
                  <span key={w} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-[11px] text-slate-700" data-testid={`worker-chip-${w}`}>
                    {wName(w)}
                    {canEdit && <button type="button" onClick={() => removeWorker(w)} className="text-slate-400 hover:text-red-500">✕</button>}
                  </span>
                ))}
                {form.workers.length === 0 && <span className="text-[11px] text-slate-400">Unassigned (open shift)</span>}
              </div>
              {canEdit && <Combobox testid="field-worker" options={wOpts.filter((o) => !form.workers.includes(o.value))} value={null} onChange={addWorker} placeholder="+ Add support worker" />}
              {form.workers.length > 1 && !form.no_participant && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 space-y-2" data-testid="worker-billing">
                  <div>
                    <Label className="text-[11px] text-slate-600">Billing with {form.workers.length} workers</Label>
                    <select value={form.billing_workers_mode || ""} disabled={!canEdit}
                      onChange={(e) => setForm((f) => ({ ...f, billing_workers_mode: e.target.value }))}
                      className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="field-billing-workers-mode">
                      <option value="">Bill once (default)</option>
                      <option value="each">Bill each worker ({form.workers.length}:1 support)</option>
                      <option value="shadow">Shadow shift (introducing a new worker)</option>
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1">
                      {form.billing_workers_mode === "each"
                        ? "The participant is charged for every worker's time. Use only when their plan funds more than one worker at once."
                        : form.billing_workers_mode === "shadow"
                          ? "Both workers are billed only if the participant has complex support needs (Invoicing → Billing setup). The NDIS allows up to 6 weekday hours a year."
                          : "The participant pays for one worker. Every worker is still paid and claims their own KM and expenses."}
                    </p>
                  </div>
                  {form.billing_workers_mode && form.is_sleepover && (
                    <label className="flex items-start gap-2 text-[11px] text-slate-700 cursor-pointer">
                      <input type="checkbox" className="accent-[#bf3b4f] mt-0.5" disabled={!canEdit} checked={!!form.sleepover_each_worker}
                        onChange={(e) => setForm((f) => ({ ...f, sleepover_each_worker: e.target.checked }))} data-testid="field-sleepover-each-worker" />
                      <span>Also bill a sleepover for each worker<span className="block text-[10px] text-slate-500">Off: one sleepover is charged. The NDIS rules don't cover two sleepover workers, so only tick this if the plan funds it.</span></span>
                    </label>
                  )}
                </div>
              )}
              {canEdit && !(isCreate && repeat.on) && form.workers.some((w) => !savedWorkers.includes(w)) && (
                <NotifyCheckboxes testid="assign-notify" value={assignNotify} onChange={setAssignNotify}
                  labels={["Notify staff of assignment", "Notify participant of assignment"]}
                  hint="Emails go to the newly added worker(s) / the participant when you save this shift." />
              )}

              {travel && (travel.prev || travel.next) && (
                <div className="space-y-1" data-testid="drawer-travel">
                  {["prev", "next"].map((side) => {
                    const t = travel[side];
                    if (!t) return null;
                    const tone = t.status === "impossible" ? { bg: "#FEF2F2", bd: "#FCA5A5", fg: "#B91C1C" }
                      : t.status === "tight" ? { bg: "#FFFBEB", bd: "#FDE68A", fg: "#92400E" }
                      : { bg: "#F8FAFC", bd: "#E2E8F0", fg: "#475569" };
                    const when = side === "prev" ? `ends ${timeOf(t.end_time)}` : `starts ${timeOf(t.start_time)}`;
                    return (
                      <div key={side} data-testid={`drawer-travel-${side}`}
                        className="flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px]"
                        style={{ backgroundColor: tone.bg, border: `1px solid ${tone.bd}`, color: tone.fg }}>
                        <Car className="h-3 w-3 mt-px shrink-0" />
                        <span>
                          <span className="font-semibold">{side === "prev" ? "From previous shift" : "To next shift"}</span>
                          {" "}({t.participant || "—"}, {when}) ·{" "}
                          {t.drive_min === null ? "drive time couldn't be estimated"
                            : `~${t.drive_min} min drive, ${t.gap_min} min gap`}
                          {t.status === "impossible" ? " — the worker can't make it in time"
                            : t.status === "tight" ? " — tight if it runs over"
                            : t.linked ? " — linked package" : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {!form.no_participant && <Field label="SIL House (optional)">
                <Combobox testid="field-sil" disabled={!canEdit}
                  options={properties.map((p) => ({ value: p.name, label: p.property_name, hint: p.suburb }))}
                  value={form.sil_property || null} onChange={pickSil}
                  placeholder="Link to a SIL house — auto-adds its participants" />
                {form.sil_property && <p className="text-[10px] text-slate-400 mt-0.5">All active tenants were added below. Set a billing type for each.</p>}
              </Field>}

              <ParticipantsBilling form={form} setForm={setForm} setLocOther={setLocOther} can={can} itemGroups={itemGroups} pOpts={pOpts} pName={pName}
                addParticipant={addParticipant} removeParticipant={removeParticipant} splitParticipant={splitParticipant} setPart={setPart} />

              <Field label="Location *">
                {locOther ? (
                  <div className="flex gap-1">
                    <AddressAutocomplete value={form.location} disabled={!canEdit}
                      onChange={(v) => setForm((f) => ({ ...f, location: v }))} testid="field-location" />
                    {addresses.length > 0 && <Button type="button" variant="outline" size="sm" className="h-9 text-[10px] px-2" onClick={() => { setLocOther(false); const p = addresses.find((a) => a.is_primary) || addresses[0]; setForm((f) => ({ ...f, location: p?.address || "" })); }}>List</Button>}
                  </div>
                ) : (
                  <select data-testid="field-location-select" value={form.location} disabled={!canEdit}
                    onChange={(e) => { if (e.target.value === "__other__") { setLocOther(true); setForm((f) => ({ ...f, location: "" })); } else setForm((f) => ({ ...f, location: e.target.value })); }}
                    className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full text-slate-700">
                    {addresses.length === 0 && <option value="">No saved addresses</option>}
                    {addresses.map((a, i) => <option key={i} value={a.address}>{`${a.label}${a.is_primary ? " (primary)" : ""} — ${a.address}`}</option>)}
                    <option value="__other__">Other / enter manually…</option>
                  </select>
                )}
              </Field>

              <label className="flex items-center gap-2 mt-2" data-testid="field-endaddr-toggle-label">
                <Switch data-testid="field-endaddr-toggle" checked={form.end_address_enabled} disabled={!canEdit}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, end_address_enabled: v }))} />
                <span className="text-xs text-slate-600">Different end address (shown to the support worker)</span>
              </label>
              {form.end_address_enabled && (
                <Field label="End address">
                  {endOther ? (
                    <div className="flex gap-1">
                      <AddressAutocomplete value={form.end_address} disabled={!canEdit}
                        onChange={(v) => setForm((f) => ({ ...f, end_address: v }))} testid="field-end-address" />
                      {addresses.length > 0 && <Button type="button" variant="outline" size="sm" className="h-9 text-[10px] px-2"
                        onClick={() => { setEndOther(false); setForm((f) => ({ ...f, end_address: "" })); }}>List</Button>}
                    </div>
                  ) : (
                    <select data-testid="field-end-address-select" value={form.end_address} disabled={!canEdit}
                      onChange={(e) => { if (e.target.value === "__other__") { setEndOther(true); setForm((f) => ({ ...f, end_address: "" })); } else setForm((f) => ({ ...f, end_address: e.target.value })); }}
                      className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full text-slate-700">
                      <option value="">Select an end address…</option>
                      {addresses.map((a, i) => <option key={i} value={a.address}>{`${a.label}${a.is_primary ? " (primary)" : ""} — ${a.address}`}</option>)}
                      <option value="__other__">Other / enter manually…</option>
                    </select>
                  )}
                </Field>
              )}

              <SectionLabel>Rostered Time</SectionLabel>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Date *">
                  <Input data-testid="field-date" type="date" value={form.shift_date} disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, shift_date: e.target.value }))} className="h-9 text-xs" />
                </Field>
                <Field label="Start *">
                  <Input data-testid="field-start" type="time" step={900} value={form.start} disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                    onBlur={(e) => setForm((f) => ({ ...f, start: snapTo15(e.target.value) }))} className="h-9 text-xs" />
                </Field>
                <Field label="End *">
                  <Input data-testid="field-end" type="time" step={900} value={form.end} disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                    onBlur={(e) => setForm((f) => ({ ...f, end: snapTo15(e.target.value) }))} className="h-9 text-xs" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <Field label="KM Authorised">
                  <Input data-testid="field-km" type="number" min="0" step="0.1" value={form.km_authorised} disabled={!canEdit || !kmAllowed}
                    onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, km_authorised: v === "" ? "" : Math.max(0, Number(v)) })); }} className="h-9 text-xs"
                    title={!kmAllowed ? (quote?.km_reason || "KM only for Activity-Based Transport or SIL") : ""} />
                  {form.participants.length > 0 && !kmAllowed && <div className="text-[10px] text-slate-400 mt-0.5">KM disabled — {quote?.km_reason || "no transport item"}</div>}
                  {kmBudget && kmOver > 0 && (
                    <div className="text-[10px] text-amber-700 mt-0.5" data-testid="km-budget-warning">
                      Only {Math.max(0, kmBudget.remaining + kmSaved).toLocaleString("en-AU", { maximumFractionDigits: 1 })} km left on this participant's agreement. This takes it {kmOver.toLocaleString("en-AU", { maximumFractionDigits: 1 })} km over.
                    </div>)}
                </Field>
                <label className="flex items-center gap-2 pb-2">
                  <Switch data-testid="field-jobboard" checked={form.job_board_open} disabled={!canEdit}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, job_board_open: v }))} />
                  <span className="text-xs text-slate-600">Open on Job Board</span>
                </label>
              </div>
              {form.job_board_open && (
                <div className="rounded-md border border-violet-200 bg-violet-50 p-2.5 space-y-2" data-testid="jobboard-targeting">
                  <div className="text-[11px] font-semibold text-violet-800">Job Board audience</div>
                  <div className="text-[10px] text-violet-700 -mt-1">Choose who can see this listing. Workers flagged incompatible with the participant are always hidden.</div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 block mb-1">Visible to gender</label>
                    <div className="flex flex-wrap gap-1.5" data-testid="field-jb-gender">
                      <button type="button" disabled={!canEdit} data-testid="jb-gender-everyone"
                        onClick={() => setForm((f) => ({ ...f, jb_target_genders: [] }))}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${(form.jb_target_genders || []).length === 0 ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200"}`}>
                        Everyone
                      </button>
                      {genderOpts.map(({ key, label, count }) => {
                        const on = (form.jb_target_genders || []).some((x) => gKey(x) === key);
                        return (
                          <button key={key} type="button" disabled={!canEdit} data-testid={`jb-gender-${label}`}
                            onClick={() => setForm((f) => {
                              const cur = (f.jb_target_genders || []).filter((x) => gKey(x) !== key);
                              return { ...f, jb_target_genders: on ? cur : [...cur, label] };
                            })}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200"}`}>
                            {label} <span className={on ? "text-violet-200" : "text-slate-400"}>({count})</span>
                          </button>
                        );
                      })}
                    </div>
                    {(form.jb_target_genders || []).length > 0 && noGenderCount > 0 && (
                      <div className="text-[10px] text-amber-700 mt-1" data-testid="jb-gender-unrecorded">
                        {noGenderCount} active support worker{noGenderCount === 1 ? " has" : "s have"} no gender recorded and won't see this listing.
                      </div>
                    )}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox data-testid="field-jb-preferred" checked={!!form.jb_preferred_only} disabled={!canEdit}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, jb_preferred_only: !!v }))} />
                    <span className="text-[11px] text-slate-700">Only preferred / team workers for this participant</span>
                  </label>
                </div>
              )}
              {form.job_board_open && (form.workers || []).length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5" data-testid="jobboard-justify">
                  <div className="text-[11px] font-semibold text-amber-800">This shift already has a worker assigned.</div>
                  <div className="text-[10px] text-amber-700 mb-1.5">Add a justification for posting it to the job board — the current worker stays attached until you assign someone else.</div>
                  <Input data-testid="field-jobboard-reason" value={form.job_board_reason || ""} disabled={!canEdit}
                    onChange={(e) => setForm((f) => ({ ...f, job_board_reason: e.target.value }))}
                    className="h-8 text-xs" placeholder="e.g. worker requested cover / seeking a second for 2:1" />
                </div>
              )}

              <SectionLabel>Shift Instructions &amp; Tasks</SectionLabel>
              <Field label="Shift instructions (visible to the worker)">
                {canEdit ? (
                  <>
                    <Textarea data-testid="field-shift-instructions" value={form.shift_instructions} rows={3}
                      onChange={(e) => setForm((f) => ({ ...f, shift_instructions: e.target.value }))}
                      className="text-xs" placeholder="Participant-specific instructions, routines, things to watch for… (links become clickable)" />
                    {urlRegex.test(form.shift_instructions || "") && (
                      <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="instructions-link-preview">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Link preview</div>
                        <Linkify text={form.shift_instructions} className="block text-xs text-slate-700 leading-relaxed"
                          linkClassName="underline text-[#bf3b4f] break-words" />
                      </div>
                    )}
                  </>
                ) : (
                  <Linkify text={form.shift_instructions || "—"} className="block text-xs text-slate-700 leading-relaxed"
                    linkClassName="underline text-[#bf3b4f] break-words" />
                )}
              </Field>
              <div className="mt-2" data-testid="field-tasks">
                <div className="text-[11px] font-semibold text-slate-600 mb-1.5">Tasks</div>
                <div className="space-y-1.5">
                  {(form.tasks || []).map((t, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`field-task-${i}`}>
                      <Input value={t.title} disabled={!canEdit} placeholder="e.g. Prepare lunch"
                        onChange={(e) => setForm((f) => ({ ...f, tasks: f.tasks.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) }))}
                        className="h-8 text-xs" data-testid={`field-task-input-${i}`} />
                      {canEdit && (
                        <button type="button" data-testid={`field-task-remove-${i}`}
                          onClick={() => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, j) => j !== i) }))}
                          className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                      )}
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] mt-2" data-testid="field-task-add"
                    onClick={() => setForm((f) => ({ ...f, tasks: [...(f.tasks || []), { title: "", completed: false, notes: "" }] }))}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add task
                  </Button>
                )}
              </div>

              <SectionLabel>Billing</SectionLabel>
              <label className="flex items-center gap-2">
                <Switch data-testid="field-sleepover" checked={form.is_sleepover} disabled={!canEdit} onCheckedChange={toggleSleepover} />
                <span className="text-xs text-slate-600">Sleepover (defaults 22:00–06:00)</span>
              </label>
              {form.is_sleepover && (
                <div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Sleepover Start"><Input type="time" step={900} value={form.sleep_start} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, sleep_start: e.target.value }))} onBlur={(e) => setForm((f) => ({ ...f, sleep_start: snapTo15(e.target.value) }))} className="h-9 text-xs" data-testid="field-sleep-start" /></Field>
                    <Field label="Sleepover End"><Input type="time" step={900} value={form.sleep_end} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, sleep_end: e.target.value }))} onBlur={(e) => setForm((f) => ({ ...f, sleep_end: snapTo15(e.target.value) }))} className="h-9 text-xs" data-testid="field-sleep-end" /></Field>
                  </div>
                  {sleepReasonRequired && (
                    <div className="mt-1 space-y-1.5" data-testid="sleep-gap-block">
                      <div className="text-[11px] text-red-500" data-testid="sleep-gap-error">
                        Sleepover must span exactly 8 hours (currently {Math.floor(sleepMins / 60)}h{sleepMins % 60 ? ` ${sleepMins % 60}m` : ""}).
                      </div>
                      {can.has("roster.override") ? (
                        <div>
                          <Label className="text-[11px] text-slate-500">Manager override justification (required)</Label>
                          <Textarea
                            data-testid="sleep-override-reason"
                            value={form.sleep_override_reason || ""}
                            disabled={!canEdit}
                            onChange={(e) => setForm((f) => ({ ...f, sleep_override_reason: e.target.value }))}
                            placeholder="Explain why this sleepover deviates from 8 hours…"
                            className="text-xs mt-1" rows={2} />
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-500">Only a Rostering Manager or Admin can override this rule.</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isCreate && can.has("roster.repeat") && (
                <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="repeat-block">
                  <label className="flex items-center gap-2">
                    <Switch data-testid="field-repeat" checked={repeat.on} onCheckedChange={(v) => setRepeat((r) => ({ ...r, on: v }))} />
                    <span className="text-xs font-medium text-slate-700">Repeat this shift (support worker left blank)</span>
                  </label>
                  {repeat.on && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Frequency">
                          <select data-testid="repeat-frequency" value={repeat.frequency}
                            onChange={(e) => setRepeat((r) => ({ ...r, frequency: e.target.value }))}
                            className="w-full h-9 text-xs rounded-md border border-slate-200 bg-white px-2">
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </Field>
                        {repeat.frequency === "weekly" && (
                          <Field label="Every N week(s)">
                            <Input type="number" min={1} data-testid="repeat-interval" value={repeat.interval}
                              onChange={(e) => setRepeat((r) => ({ ...r, interval: e.target.value }))} className="h-9 text-xs" />
                          </Field>
                        )}
                        {repeat.frequency === "monthly" && (
                          <Field label="Monthly on">
                            <select data-testid="repeat-monthly-mode" value={repeat.monthly_mode}
                              onChange={(e) => setRepeat((r) => ({ ...r, monthly_mode: e.target.value }))}
                              className="w-full h-9 text-xs rounded-md border border-slate-200 bg-white px-2">
                              <option value="day_of_month">Same day of month</option>
                              <option value="weekday">Same weekday (e.g. 3rd Thu)</option>
                            </select>
                          </Field>
                        )}
                      </div>
                      {repeat.frequency === "weekly" && (
                        <Field label="Repeat on (leave blank = same weekday as the shift)">
                          <div className="flex flex-wrap gap-1" data-testid="repeat-weekdays">
                            {[["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]].map(([lb, wd]) => {
                              const on = repeat.weekdays.includes(wd);
                              return (
                                <button key={wd} type="button" data-testid={`repeat-weekday-${wd}`}
                                  onClick={() => setRepeat((r) => ({ ...r, weekdays: on ? r.weekdays.filter((x) => x !== wd) : [...r.weekdays, wd] }))}
                                  className={`h-8 w-10 rounded text-[11px] font-medium border transition-colors ${on ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                                  {lb}
                                </button>
                              );
                            })}
                          </div>
                        </Field>
                      )}
                      <Field label="Repeat until (required)">
                        <Input type="date" data-testid="repeat-until" value={repeat.until} min={form.shift_date}
                          onChange={(e) => setRepeat((r) => ({ ...r, until: e.target.value }))} className="h-9 text-xs" />
                      </Field>
                      <div className="text-[10px] text-slate-400">Each generated shift is a Draft with no worker assigned — fill workers in later.</div>
                    </div>
                  )}
                </div>
              )}

              <CostBreakdown quote={quote} />

              {canEdit && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: "#bf3b4f" }} onClick={save} disabled={saving || sleepBlocked} data-testid="save-shift">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" />{isCreate ? (repeat.on ? "Create repeating shifts" : "Create shift") : "Save changes"}</>}
                  </Button>
                </div>
              )}
            </div>

            {/* Linked package */}
            {!isCreate && canEdit && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="linked-package-section">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-semibold text-indigo-900">Linked Package</span>
                </div>
                {shift?.link_group_id ? (
                  <div className="space-y-2">
                    <p className="text-xs text-indigo-800">This shift is part of a linked, back-to-back package (leg {shift?.link_seq || "?"}). One worker covers every leg; staffing is mirrored across them and the provider-travel short charge uses the combined duration.</p>
                    {linkedLegs.length > 0 && (
                      <div className="space-y-1.5" data-testid="linked-legs">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700/70">Linked shifts</div>
                        {linkedLegs.map((l) => (
                          <button
                            key={l.name}
                            type="button"
                            onClick={() => onOpenShift?.(l.name)}
                            disabled={!onOpenShift}
                            data-testid={`linked-leg-${l.name}`}
                            className="w-full flex items-center justify-between gap-2 rounded-md border border-indigo-200 bg-white px-2.5 py-1.5 text-left transition-colors enabled:hover:border-indigo-400 enabled:hover:bg-indigo-50 disabled:cursor-default">
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-indigo-800 truncate flex items-center gap-1.5">
                                {l.link_seq ? <span className="text-[10px] font-semibold text-indigo-500">Leg {l.link_seq}</span> : null}
                                {l.name}
                              </div>
                              <div className="text-[10px] text-slate-500 truncate">
                                {l.participant_name ? `${l.participant_name} · ` : ""}{timeOf(l.start_time)}–{timeOf(l.end_time)}{l.support_worker_name ? ` · ${l.support_worker_name}` : " · unassigned"}
                              </div>
                            </div>
                            {onOpenShift && <ExternalLink className="h-3.5 w-3.5 text-indigo-500 shrink-0" />}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="h-8 text-xs border-indigo-300 text-indigo-700" onClick={doUnlink} disabled={linking} data-testid="unlink-shift-btn">
                        {linking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1.5" />} Unlink this shift
                      </Button>
                      {can.has("roster.repeat") && (
                        <Button variant="outline" size="sm" className="h-8 text-xs border-indigo-300 text-indigo-700" onClick={openLinkRepeat} disabled={linking} data-testid="link-repeat-btn">
                          <Link2 className="h-3.5 w-3.5 mr-1.5" /> Make recurring
                        </Button>
                      )}
                    </div>
                  </div>
                ) : linkCands === null ? (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-600">Combine this with a shift that runs exactly back-to-back at the same location, so a single worker covers both as one package.</p>
                    <Button variant="outline" size="sm" className="h-8 text-xs border-indigo-300 text-indigo-700" onClick={loadLinkCands} data-testid="find-link-btn">
                      <Link2 className="h-3.5 w-3.5 mr-1.5" /> Link to an adjacent shift
                    </Button>
                  </div>
                ) : linkCands.length === 0 ? (
                  <p className="text-xs text-slate-500" data-testid="no-link-candidates">No shift runs exactly back-to-back at this location. Linkable shifts must have zero gap and the same location.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="link-candidates">
                    {linkCands.map((c) => (
                      <div key={c.name} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-slate-800 truncate">{c.participant_name || c.name}</div>
                          <div className="text-[10px] text-slate-500">{timeOf(c.start_time)}–{timeOf(c.end_time)}{c.support_worker_name ? ` · ${c.support_worker_name}` : " · unassigned"}{c.already_linked ? " · already a package" : ""}</div>
                        </div>
                        <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => doLink(c.name)} disabled={linking} data-testid={`link-to-${c.name}`}>Link</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Lifecycle actions */}
            {!isCreate && canEdit && (
              <LifecycleActions status={status} can={can} shift={shift} seriesId={seriesId} shiftName={shiftName}
                doPublish={doPublish} doUnpublish={doUnpublish} openReason={openReason} runAction={runAction} onOpenChange={onOpenChange}
                onDeleteSeries={() => setDelSeriesDlg({ from: shift?.shift_date || orgToday() })}
                onClone={() => setCloneDlg({ date: shift?.shift_date || orgToday() })} />
            )}

            {/* Applications */}
            {!isCreate && (
              <ShiftApplications shift={shift} can={can}
                onApprove={(sw) => runAction(() => api.approveApp(shiftName, sw), "Application approved & worker assigned")} />
            )}

            {/* Audit trail */}
            {!isCreate && <ShiftAudit entries={shift?.audit_entries} />}

            {/* Worker shift notes & attachments — read-only view of what workers submitted */}
            {!isCreate && shiftNotes.length > 0 && (
              <div data-testid="drawer-shift-notes-section">
                <SectionLabel>Shift Notes & Attachments</SectionLabel>
                <div className="space-y-2 mt-1">
                  {shiftNotes.map((n) => (
                    <div key={n.name} data-testid={`drawer-shift-note-${n.participant}`} className="rounded-md border border-slate-200 bg-white p-2.5 space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-slate-700">{n.participant_name || n.participant}</span>
                        <span className="text-[10px] text-slate-400">{n.support_worker_name || ""}{n.status ? ` · ${n.status}` : ""}</span>
                      </div>
                      {n.urgent_notes && <div className="text-[11px]"><span className="font-semibold text-amber-600">Important: </span><span className="text-amber-700 whitespace-pre-wrap">{n.urgent_notes}</span></div>}
                      <div className="text-[11px] text-slate-600 whitespace-pre-wrap">{n.events || <span className="text-slate-300">No events recorded.</span>}</div>
                      {n.mood && <div className="text-[11px] text-slate-500 whitespace-pre-wrap"><span className="font-semibold">Mood: </span>{n.mood}</div>}
                      {(n.attachments || []).length > 0 && (
                        <ShiftNoteAttachments items={n.attachments} load={(idx, preview) => api.shiftNoteAttachment(n.name, idx, preview)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Office notes — internal audit log, never shown to the support worker */}
            {!isCreate && canEdit && (
              <div data-testid="office-notes-section">
                <SectionLabel>Office Notes (internal · not visible to worker)</SectionLabel>
                <div className="space-y-1.5 mt-1">
                  {(shift?.office_notes || []).length === 0 && (
                    <div className="text-[11px] text-slate-400" data-testid="office-notes-empty">No office notes yet.</div>
                  )}
                  {(shift?.office_notes || []).map((n, i) => (
                    <div key={i} data-testid={`office-note-${i}`} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                      <p className="text-xs text-slate-700 whitespace-pre-wrap leading-snug">{n.note}</p>
                      <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-400">
                        <Lock className="h-2.5 w-2.5" />
                        <span>{n.added_by || "Office"}</span>
                        {n.added_on && <span>· {fmtLogFull(n.added_on)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 space-y-1.5">
                  <Textarea data-testid="office-note-input" value={officeNote} rows={2}
                    onChange={(e) => setOfficeNote(e.target.value)}
                    placeholder="Add an internal note for the audit trail…" className="text-xs" />
                  <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="office-note-add"
                    onClick={addOfficeNote} disabled={addingNote || !officeNote.trim()}>
                    {addingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><StickyNote className="h-3.5 w-3.5 mr-1.5" /> Add to audit log</>}
                  </Button>
                  <div className="text-[10px] text-slate-400">Once added, a note is locked and can't be edited or removed.</div>
                </div>
              </div>
            )}
          </div>
        )}

        <BudgetCheckDialog
          dlg={budgetDlg} canOverride={can.has("roster.budget_override")}
          onCancel={() => setBudgetDlg(null)}
          onConfirm={(res) => { const retry = budgetDlg.retry; budgetRes.current = res; setBudgetDlg(null); retry(); }} />

        <ShiftDialogs
          dupDlg={dupDlg} setDupDlg={setDupDlg} doRepeatCreate={doRepeatCreate} saving={saving}
          scopeDlg={scopeDlg} setScopeDlg={setScopeDlg} doUpdate={doUpdate}
          scopeHasWorker={!!(form.workers && form.workers.length)} scopeWorkerName={form.workers?.[0] ? wName(form.workers[0]) : (shift?.support_worker_name || "")}
          includeWorker={includeWorker} setIncludeWorker={setIncludeWorker}
          delSeriesDlg={delSeriesDlg} setDelSeriesDlg={setDelSeriesDlg} doDeleteSeries={doDeleteSeries}
          reasonDlg={reasonDlg} setReasonDlg={setReasonDlg} reason={reason} setReason={setReason} confirmReason={confirmReason}
          cancelCharge={cancelCharge} setCancelCharge={setCancelCharge}
          cancelInitiator={cancelInitiator} setCancelInitiator={setCancelInitiator}
          cancelNotify={cancelNotify} setCancelNotify={setCancelNotify}
          cloneDlg={cloneDlg} setCloneDlg={setCloneDlg} doClone={doClone} cloning={cloning} />

        <Dialog open={kmWarn} onOpenChange={(v) => { if (!v) setKmWarn(false); }}>
          <DialogContent data-testid="km-warn-dialog">
            <DialogHeader>
              <DialogTitle>Remove KM allowance?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              You have <b>{form.km_authorised} km</b> authorised, but the billing type you just selected doesn't allow
              Activity Based Transport — there's no claimable item group left for these kilometres.
              Continuing will remove the authorised KM. Cancel to keep your previous billing selection and KM.
            </p>
            <DialogFooter>
              <Button variant="outline" data-testid="km-warn-cancel" onClick={() => {
                if (partsSnapshotRef.current) setForm((f) => ({ ...f, participants: partsSnapshotRef.current }));
                setKmWarn(false);
              }}>Cancel &amp; keep</Button>
              <Button style={{ backgroundColor: "#bf3b4f" }} data-testid="km-warn-continue" onClick={() => {
                setForm((f) => ({ ...f, km_authorised: 0 }));
                setKmWarn(false);
              }}>Continue &amp; remove KM</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!lrepeat} onOpenChange={(v) => { if (!v) setLrepeat(null); }}>
          <DialogContent data-testid="link-repeat-dialog">
            <DialogHeader>
              <DialogTitle>Make this linked package recurring</DialogTitle>
            </DialogHeader>
            {lrepeat && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Every occurrence is auto-created as its own linked package (all legs, times matched, no worker assigned — fill workers in later).</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Frequency">
                    <select data-testid="lrepeat-frequency" value={lrepeat.frequency}
                      onChange={(e) => setLrepeat((r) => ({ ...r, frequency: e.target.value }))}
                      className="w-full h-9 text-xs rounded-md border border-slate-200 bg-white px-2">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </Field>
                  {lrepeat.frequency === "weekly" && (
                    <Field label="Every N week(s)">
                      <Input type="number" min={1} data-testid="lrepeat-interval" value={lrepeat.interval}
                        onChange={(e) => setLrepeat((r) => ({ ...r, interval: e.target.value }))} className="h-9 text-xs" />
                    </Field>
                  )}
                  {lrepeat.frequency === "monthly" && (
                    <Field label="Monthly on">
                      <select data-testid="lrepeat-monthly-mode" value={lrepeat.monthly_mode}
                        onChange={(e) => setLrepeat((r) => ({ ...r, monthly_mode: e.target.value }))}
                        className="w-full h-9 text-xs rounded-md border border-slate-200 bg-white px-2">
                        <option value="day_of_month">Same day of month</option>
                        <option value="weekday">Same weekday (e.g. 3rd Thu)</option>
                      </select>
                    </Field>
                  )}
                </div>
                {lrepeat.frequency === "weekly" && (
                  <Field label="Repeat on">
                    <div className="flex flex-wrap gap-1" data-testid="lrepeat-weekdays">
                      {[["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]].map(([lb, wd]) => {
                        const on = lrepeat.weekdays.includes(wd);
                        return (
                          <button key={wd} type="button" data-testid={`lrepeat-weekday-${wd}`}
                            onClick={() => setLrepeat((r) => ({ ...r, weekdays: on ? r.weekdays.filter((x) => x !== wd) : [...r.weekdays, wd] }))}
                            className={`h-8 w-10 rounded text-[11px] font-medium border transition-colors ${on ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                            {lb}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                )}
                <Field label="Repeat until (required)">
                  <Input type="date" data-testid="lrepeat-until" value={lrepeat.until} min={shift?.shift_date}
                    onChange={(e) => setLrepeat((r) => ({ ...r, until: e.target.value }))} className="h-9 text-xs" />
                </Field>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" data-testid="lrepeat-cancel" onClick={() => setLrepeat(null)}>Cancel</Button>
              <Button style={{ backgroundColor: "#bf3b4f" }} data-testid="lrepeat-create" disabled={linking || !lrepeat?.until}
                onClick={() => doLinkRepeat("warn")}>
                {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create recurring packages"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!lrepeatDup} onOpenChange={(v) => { if (!v) setLrepeatDup(null); }}>
          <DialogContent data-testid="lrepeat-dup-dialog">
            <DialogHeader>
              <DialogTitle>Some dates already have matching shifts</DialogTitle>
            </DialogHeader>
            {lrepeatDup && (
              <div className="space-y-3">
                <p className="text-xs text-slate-600">
                  {lrepeatDup.dates.length} of {lrepeatDup.total} occurrence date(s) already have shifts matching this package (e.g. from an existing recurring series):
                </p>
                <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 max-h-28 overflow-y-auto" data-testid="lrepeat-dup-dates">
                  {lrepeatDupDates}
                </div>
                <p className="text-xs text-slate-500">How should these be handled?</p>
                <div className="space-y-2">
                  <Button className="w-full justify-start h-auto py-2 text-left" style={{ backgroundColor: "#bf3b4f" }}
                    data-testid="lrepeat-dup-link-existing" disabled={linking} onClick={() => doLinkRepeat("link_existing")}>
                    <div><div className="text-xs font-semibold">Link the existing shifts</div>
                      <div className="text-[10px] opacity-80 font-normal">Join the shifts already on those dates into the package (recommended)</div></div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-2 text-left"
                    data-testid="lrepeat-dup-create" disabled={linking} onClick={() => doLinkRepeat("create")}>
                    <div><div className="text-xs font-semibold">Create new shifts anyway</div>
                      <div className="text-[10px] text-slate-500 font-normal">Adds a second set of shifts alongside the existing ones</div></div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-2 text-left"
                    data-testid="lrepeat-dup-skip" disabled={linking} onClick={() => doLinkRepeat("skip")}>
                    <div><div className="text-xs font-semibold">Skip those dates</div>
                      <div className="text-[10px] text-slate-500 font-normal">Only create packages on dates that are currently empty</div></div>
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="ghost" data-testid="lrepeat-dup-cancel" onClick={() => setLrepeatDup(null)} disabled={linking}>Cancel</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
